// adminportal/src/pages/CreateUserPage.tsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import CompanySearchDropdown from '../components/CompanySearchDropdown';
import SharedCreateCompanyModal from '../components/SharedCreateCompanyModal';
import { apiUrl } from '../config';
import Copyright from '../components/Copyright';

interface Company {
  companyId: string;
  companyName: string;
  companyUrl: string;
  companyState: string;
  isPrimary?: boolean;
  primaryContact?: string;
  primaryPhone?: string;
  primaryEmail?: string;
  sftpAuthType?: 'password' | 'ssh-key';
  sftpUsername?: string;
  sftpPassword?: string;
  sshPublicKey?: string;
}

export default function CreateUserPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyId: '',
    companyName: '',
    roleId: '',
    mfaRequired: false,
    passwordOption: 'temporary' as 'temporary' | 'permanent' | 'generated',
    customPassword: '',
    sendWelcomeEmail: true,
  });
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [showEmailValidationModal, setShowEmailValidationModal] = useState(false);
  const [emailValidationMessage, setEmailValidationMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCompanySelect = (companyId: string, company: Company | null) => {
    setSelectedCompany(company);
    setForm(prev => ({
      ...prev,
      companyId,
      companyName: company?.companyName || '',
    }));
  };

  const handleNewCompanyRequest = () => {
    setShowCreateCompanyModal(true);
  };

  const handleCompanyCreated = (newCompany: Company) => {
    setSelectedCompany(newCompany);
    setForm(prev => ({
      ...prev,
      companyId: newCompany.companyId,
      companyName: newCompany.companyName,
    }));
    setShowCreateCompanyModal(false);
  };

  const validateEmailDomain = () => {
    if (!selectedCompany || !selectedCompany.companyUrl) return true;

    const emailDomain = form.email.split('@')[1]?.toLowerCase();
    if (!emailDomain) return true;

    // Extract company domain from companyUrl
    let companyDomain = '';
    try {
      // Handle URLs with or without protocol
      const urlToProcess = selectedCompany.companyUrl;
      if (!urlToProcess.startsWith('http://') && !urlToProcess.startsWith('https://')) {
        // If no protocol, treat as hostname
        companyDomain = urlToProcess.split('/')[0].toLowerCase();
      } else {
        // If has protocol, parse as URL
        const url = new URL(urlToProcess);
        companyDomain = url.hostname.toLowerCase();
      }
    } catch {
      // Fallback: extract domain from any format
      companyDomain = selectedCompany.companyUrl
        .replace(/^https?:\/\//, '')
        .split('/')[0]
        .toLowerCase();
    }

    // Check if email domain matches company domain - but only show warning, don't block
    if (companyDomain && !emailDomain.endsWith(companyDomain)) {
      setEmailValidationMessage(
        `Note: Email domain ${emailDomain} differs from company domain ${companyDomain}. This is usually fine, but ensure the email is correct.`
      );
      setShowEmailValidationModal(true);
      // Return true to allow submission - this is just a warning
      return true;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    // Validate form
    if (
      !form.firstName ||
      !form.lastName ||
      !form.email ||
      !form.companyId ||
      !form.companyName ||
      !form.roleId
    ) {
      setErrorMessage('Please fill in all required fields');
      setIsLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setErrorMessage('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    // Validate email domain matches company
    if (!validateEmailDomain()) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(apiUrl('create-user'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          companyId: form.companyId,
          companyName: form.companyName,
          roleId: form.roleId,
          mfaRequired: form.mfaRequired,
          passwordOption: form.passwordOption,
          customPassword: form.customPassword || undefined,
          sendWelcomeEmail: form.sendWelcomeEmail,
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'User creation failed');
      }

      const data = await res.json();
      // User created successfully

      // Navigate to dashboard after successful creation
      navigate('/admin-dashboard', {
        state: {
          message: `✅ User "${data.user.firstName} ${data.user.lastName}" created successfully`,
          user: data.user,
          password: data.user.password,
          passwordOption: form.passwordOption,
          sendWelcomeEmail: form.sendWelcomeEmail,
        },
      });
    } catch (err: unknown) {
      // Error creating user
      setErrorMessage(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='p-4 max-w-lg mx-auto'>
      <div className='flex items-center justify-between mb-4'>
        <h1 className='text-xl font-semibold'>Create New User</h1>
        <Link to='/admin-dashboard' className='text-blue-500 hover:text-blue-600 text-sm'>
          ← Back to Dashboard
        </Link>
      </div>
      <form onSubmit={handleSubmit} className='space-y-4'>
        {/* Basic User Information */}
        <div className='space-y-3'>
          <label className='block text-sm font-medium text-slate-200'>First Name</label>
          <input
            name='firstName'
            placeholder='First Name'
            value={form.firstName}
            onChange={handleChange}
            className='w-full p-2 border rounded focus:outline-none focus:ring-2 form-input'
            required
          />
          <label className='block text-sm font-medium text-slate-200'>Last Name</label>
          <input
            name='lastName'
            placeholder='Last Name'
            value={form.lastName}
            onChange={handleChange}
            className='w-full p-2 border rounded focus:outline-none focus:ring-2 form-input'
            required
          />
          <label className='block text-sm font-medium text-slate-200'>
            Email Address <span className='text-red-400'>*</span>
          </label>
          <input
            name='email'
            type='email'
            placeholder='Email Address'
            value={form.email}
            onChange={handleChange}
            className='w-full p-2 border rounded focus:outline-none focus:ring-2 form-input'
            required
          />
          <p className='text-xs text-slate-400'>
            Email is used as your unique identifier and for company access
          </p>
        </div>

        {/* Company Selection */}
        <div className='space-y-3'>
          <label className='block text-sm font-medium text-slate-200'>Company</label>
          <CompanySearchDropdown
            value={form.companyName}
            onChange={handleCompanySelect}
            onNewCompanyRequest={handleNewCompanyRequest}
            placeholder='Search for a company...'
            required
          />

          {selectedCompany && (
            <div className='p-3 bg-white/5 border border-white/10 rounded'>
              <div className='text-sm font-medium text-slate-100'>
                {selectedCompany.companyName}
              </div>
              <div className='text-xs text-slate-300'>
                {selectedCompany.companyState} • {selectedCompany.companyUrl}
              </div>
              {selectedCompany.primaryContact && (
                <div className='text-xs text-slate-400'>
                  Contact: {selectedCompany.primaryContact}
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Role Selection */}
        <div className='space-y-3'>
          <label className='block text-sm font-medium text-slate-200'>User Role</label>

          <select
            name='roleId'
            value={form.roleId}
            onChange={e => setForm(prev => ({ ...prev, roleId: e.target.value }))}
            className='w-full p-2 border rounded focus:outline-none focus:ring-2 form-select'
            aria-label='Select user role'
            required
          >
            <option value='' className='form-option'>
              Select a role
            </option>
            <option value='standard-user' className='form-option'>
              Standard User - Company shared folders (read) + own folders (full)
            </option>
            <option value='super-user' className='form-option'>
              Super User - Company folders (full) + own folders (full)
            </option>
            <option
              value='admin'
              disabled={selectedCompany ? !selectedCompany.isPrimary : false}
              className='form-option'
            >
              Admin - All companies and all users (full access)
              {selectedCompany && !selectedCompany.isPrimary && ' (Primary company only)'}
            </option>
          </select>

          <p className='text-sm text-slate-400'>
            {form.roleId === 'standard-user' &&
              'Access to company inbound/outbound (read-only) + personal folders (full access)'}
            {form.roleId === 'super-user' &&
              'Access to company folders (full) + personal folders (full access)'}
            {form.roleId === 'admin' && 'Access to everything across all companies (full access)'}
          </p>

          {selectedCompany && selectedCompany.isPrimary === false && (
            <p className='text-sm text-amber-400 bg-amber-900/20 p-2 rounded border border-amber-700/50'>
              ⚠️ Admin role is only available for users in the primary company
            </p>
          )}
        </div>

        {/* Password Options */}
        <div className='space-y-3'>
          <label className='block text-sm font-medium text-slate-200'>Password Options</label>

          <div className='space-y-3'>
            <div className='space-y-2'>
              <label className='flex items-center'>
                <input
                  type='radio'
                  name='passwordOption'
                  value='temporary'
                  checked={form.passwordOption === 'temporary'}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      passwordOption: e.target.value as 'temporary' | 'permanent' | 'generated',
                    }))
                  }
                  className='mr-2 w-4 h-4 text-blue-600 bg-slate-700 border-white/10 focus:ring-blue-500 focus:ring-2'
                />
                <span className='text-sm text-slate-200'>
                  Temporary Password (User must change on first login)
                </span>
              </label>
              <p className='text-xs text-slate-400 ml-6'>
                User will be prompted to set a new password when they first log in
              </p>
            </div>

            <div className='space-y-2'>
              <label className='flex items-center'>
                <input
                  type='radio'
                  name='passwordOption'
                  value='permanent'
                  checked={form.passwordOption === 'permanent'}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      passwordOption: e.target.value as 'temporary' | 'permanent' | 'generated',
                    }))
                  }
                  className='mr-2 w-4 h-4 text-blue-600 bg-slate-700 border-white/10 focus:ring-blue-500 focus:ring-2'
                />
                <span className='text-sm text-slate-200'>
                  Permanent Password (No reset required)
                </span>
              </label>
              <p className='text-xs text-slate-400 ml-6'>
                User can login immediately without changing password
              </p>
            </div>

            <div className='space-y-2'>
              <label className='flex items-center'>
                <input
                  type='radio'
                  name='passwordOption'
                  value='generated'
                  checked={form.passwordOption === 'generated'}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      passwordOption: e.target.value as 'temporary' | 'permanent' | 'generated',
                    }))
                  }
                  className='mr-2 w-4 h-4 text-blue-600 bg-slate-700 border-white/10 focus:ring-blue-500 focus:ring-2'
                />
                <span className='text-sm text-slate-200'>System Generated Password</span>
              </label>
              <p className='text-xs text-slate-400 ml-6'>
                System will generate a secure password automatically
              </p>
            </div>
          </div>

          {(form.passwordOption === 'temporary' || form.passwordOption === 'permanent') && (
            <div className='space-y-2'>
              <label className='block text-sm font-medium text-slate-200'>
                Custom Password (Optional)
              </label>
              <input
                type='password'
                name='customPassword'
                placeholder='Leave empty to auto-generate'
                autoComplete='new-password'
                value={form.customPassword}
                onChange={handleChange}
                className='w-full p-2 border rounded focus:outline-none focus:ring-2 form-input'
              />
              <p className='text-xs text-slate-400'>
                If left empty, a secure password will be generated automatically
              </p>
            </div>
          )}
        </div>

        {/* Email Options */}
        <div className='space-y-3'>
          <label className='block text-sm font-medium text-slate-200'>Email Options</label>

          <div className='space-y-2'>
            <label className='flex items-center'>
              <input
                type='checkbox'
                name='sendWelcomeEmail'
                checked={form.sendWelcomeEmail}
                onChange={handleChange}
                className='mr-2 w-4 h-4 text-blue-600 bg-slate-700 border-white/10 rounded focus:ring-blue-500 focus:ring-2'
              />
              <span className='text-sm text-slate-200'>
                Send welcome email with login instructions
              </span>
            </label>
            <p className='text-xs text-slate-400'>
              Includes login credentials, SFTP connection details, and setup instructions
            </p>
          </div>
        </div>

        {/* MFA Settings */}
        <div className='space-y-3'>
          <label className='block text-sm font-medium text-slate-200'>
            Multi-Factor Authentication (MFA)
          </label>

          <div className='space-y-2'>
            <label className='flex items-center'>
              <input
                type='checkbox'
                name='mfaRequired'
                checked={form.mfaRequired}
                onChange={handleChange}
                className='mr-2 w-4 h-4 text-blue-600 bg-slate-700 border-white/10 rounded focus:ring-blue-500 focus:ring-2'
              />
              <span className='text-sm text-slate-200'>Require MFA for this user</span>
            </label>

            {(form.roleId === 'admin' || form.roleId === 'super-user') && (
              <div className='p-3 bg-amber-500/20 border border-amber-500/30 rounded'>
                <p className='text-xs text-amber-200'>
                  🔒 <strong>Security Recommendation:</strong> MFA is strongly recommended for{' '}
                  {form.roleId === 'admin' ? 'admin' : 'super'} users due to their elevated system
                  access. This helps protect sensitive company data and system configurations.
                </p>
              </div>
            )}

            <p className='text-xs text-slate-400'>
              MFA adds an extra layer of security by requiring a second form of authentication
              (e.g., SMS, authenticator app). Recommended for all users, especially those with
              elevated permissions.
            </p>
          </div>
        </div>

        {errorMessage && <p className='text-sm text-red-500'>{errorMessage}</p>}

        <div className='flex gap-3'>
          <Link to='/' className='flex-1'>
            <button
              type='button'
              className='w-full px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50'
            >
              Cancel
            </button>
          </Link>
          <button
            type='submit'
            disabled={isLoading}
            className='flex-1 bg-green-600 text-white py-2 rounded disabled:opacity-50'
          >
            {isLoading ? 'Creating...' : 'Create User'}
          </button>
        </div>
      </form>

      {/* Create Company Modal */}
      <SharedCreateCompanyModal
        isOpen={showCreateCompanyModal}
        onClose={() => setShowCreateCompanyModal(false)}
        onCompanyCreated={handleCompanyCreated}
        includeServiceAccount={true}
      />

      {/* Email Validation Modal */}
      {showEmailValidationModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-blue-50 p-6 rounded-lg shadow-xl max-w-md w-full border border-blue-200'>
            <h3 className='text-lg font-semibold mb-2 text-blue-800'>Email Domain Note</h3>
            <p className='text-sm text-blue-700 mb-4'>{emailValidationMessage}</p>
            <div className='flex justify-end'>
              <button
                onClick={() => setShowEmailValidationModal(false)}
                className='px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copyright with Easter egg */}
      <Copyright className='mt-8' showEasterEgg={true} />
    </div>
  );
}

/*
 * OHIT Watermark - Optimum Healthcare IT Transfer System
 * Copyright © 2025 Optimum Healthcare IT. All rights reserved.
 *
 * This file is part of the OHIT Admin Portal for secure healthcare file transfer.
 * Built with React, TypeScript, and AWS CDK for HIPAA-compliant operations.
 *
 * OHIT: Optimized for Health Information Technology
 * Secure, reliable, and compliant file transfer solutions for healthcare organizations.
 */
